import type {
  TradeOrder,
  VybeDealInternal,
  VybeOrderInternal,
} from "@/vybe-trader-client/trade-history/types";
import { formatDirection, VybeDealType, VybeOrderType } from "@/vybe-trader-client/trade-history/types";

export interface PositionTableRow extends TradeOrder {
  formattedOpenTime: { date: string; time: string };
  formattedCloseTime: { date: string; time: string };
  displayType: string;
  isBalanceOperation: boolean;
}

export interface OrderTableRow extends VybeOrderInternal {
  formattedTime: { date: string; time: string };
  formattedSecondTime: string;
  displayType: string;
  statusLabel: string;
  statusColor: string;
}

export interface DealTableRow extends VybeDealInternal {
  formattedTime: { date: string; time: string };
  displayType: string;
}

export function formatDateTimeCompact(parts: { date: string; time: string }) {
  return `${parts.date} ${parts.time}`;
}

export function formatHistoryPrice(value: number, digits: number): string {
  return value > 0 ? value.toFixed(digits) : "-";
}

function isBuyOrderType(orderType: number): boolean {
  switch (orderType) {
    case VybeOrderType.Buy:
    case VybeOrderType.BuyLimit:
    case VybeOrderType.BuyStop:
    case VybeOrderType.BuyStopLimit:
      return true;
    default:
      return false;
  }
}

function isSellOrderType(orderType: number): boolean {
  switch (orderType) {
    case VybeOrderType.Sell:
    case VybeOrderType.SellLimit:
    case VybeOrderType.SellStop:
    case VybeOrderType.SellStopLimit:
      return true;
    default:
      return false;
  }
}

function formatSignedPercent(value: number): string {
  const abs = Math.abs(value);
  const decimals = abs >= 10 ? 2 : abs >= 1 ? 3 : 4;
  const s = value.toFixed(decimals);
  return (value >= 0 ? "+" : "") + s + "%";
}

export function positionPriceChangeParts(
  p: TradeOrder,
  isBalanceOperation: boolean
): { display: string; numeric: number | null } {
  if (isBalanceOperation || p.openPrice === 0)
    return { display: "-", numeric: null };
  const open = p.openPrice;
  const close = p.closePrice;
  let pct: number;
  if (isBuyOrderType(p.orderType)) {
    pct = ((close - open) / open) * 100;
  } else if (isSellOrderType(p.orderType)) {
    pct = ((open - close) / open) * 100;
  } else {
    return { display: "-", numeric: null };
  }
  if (!Number.isFinite(pct)) return { display: "-", numeric: null };
  return { display: formatSignedPercent(pct), numeric: pct };
}

export function dealPriceChangeParts(d: VybeDealInternal): {
  display: string;
  numeric: number | null;
} {
  if (d.openPrice === 0) return { display: "-", numeric: null };
  const open = d.openPrice;
  const price = d.price;
  const isBuy =
    d.type === VybeDealType.DealBuy || d.type === VybeDealType.CanceledBuy;
  const isSell =
    d.type === VybeDealType.DealSell || d.type === VybeDealType.CanceledSell;
  let pct: number;
  if (isBuy) {
    pct = ((price - open) / open) * 100;
  } else if (isSell) {
    pct = ((open - price) / open) * 100;
  } else {
    return { display: "-", numeric: null };
  }
  if (!Number.isFinite(pct)) return { display: "-", numeric: null };
  return { display: formatSignedPercent(pct), numeric: pct };
}

function formatPositionProfit(profit: number): string {
  return `${profit >= 0 ? "+" : ""}${profit.toFixed(2)}`;
}

export function processHistoryTableRows<T>(
  rows: T[],
  sortColumn: string | null,
  sortDirection: "asc" | "desc",
  filters: Record<string, string>,
  getSortValue: (row: T, column: string) => string | number,
  getFilterHaystack: (row: T, column: string) => string
): T[] {
  let filtered = [...rows];

  Object.entries(filters).forEach(([column, value]) => {
    if (!value) return;
    const q = value.toLowerCase();
    filtered = filtered.filter((row) =>
      getFilterHaystack(row, column).includes(q)
    );
  });

  if (sortColumn) {
    filtered.sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }

  return filtered;
}

export function getPositionRowSortValue(
  row: PositionTableRow,
  column: string
): string | number {
  switch (column) {
    case "openTime":
      return new Date(row.openTime).getTime();
    case "symbol":
      return row.symbol.toLowerCase();
    case "ticket":
      return row.ticket;
    case "type":
      return row.displayType.toLowerCase();
    case "lots":
      return row.lots;
    case "openPrice":
      return row.isBalanceOperation ? -1 : row.openPrice;
    case "tp":
      return row.takeProfit;
    case "sl":
      return row.stopLoss;
    case "closeTime":
      return new Date(row.closeTime).getTime();
    case "closePrice":
      return row.isBalanceOperation ? -1 : row.closePrice;
    case "commission":
      return row.commission;
    case "swap":
      return row.swap;
    case "pnl":
      return row.profit;
    case "change": {
      const change = positionPriceChangeParts(row, row.isBalanceOperation);
      return change.numeric ?? -Infinity;
    }
    default:
      return "";
  }
}

export function getPositionRowFilterHaystack(
  row: PositionTableRow,
  column: string
): string {
  switch (column) {
    case "openTime":
      return formatDateTimeCompact(row.formattedOpenTime).toLowerCase();
    case "symbol":
      return row.symbol.toLowerCase();
    case "ticket":
      return String(row.ticket);
    case "type":
      return row.displayType.toLowerCase();
    case "lots":
      return String(row.lots);
    case "openPrice":
      return row.isBalanceOperation
        ? "-"
        : row.openPrice.toFixed(row.digits);
    case "tp":
      return formatHistoryPrice(row.takeProfit, row.digits);
    case "sl":
      return formatHistoryPrice(row.stopLoss, row.digits);
    case "closeTime":
      return formatDateTimeCompact(row.formattedCloseTime).toLowerCase();
    case "closePrice":
      return row.isBalanceOperation
        ? "-"
        : row.closePrice.toFixed(row.digits);
    case "commission":
      return row.commission.toFixed(2);
    case "swap":
      return row.swap.toFixed(2);
    case "pnl":
      return formatPositionProfit(row.profit);
    case "change":
      return positionPriceChangeParts(row, row.isBalanceOperation).display.toLowerCase();
    default:
      return "";
  }
}

export function getOrderRowSortValue(
  row: OrderTableRow,
  column: string
): string | number {
  switch (column) {
    case "open":
      return row.openTimeMs;
    case "symbol":
      return row.symbol.toLowerCase();
    case "ticket":
      return row.ticketNumber ?? row.ticket;
    case "type":
      return row.displayType.toLowerCase();
    case "lots":
      return row.lots;
    case "price":
      return row.price;
    case "tp":
      return row.takeProfit;
    case "sl":
      return row.stopLoss;
    case "exec":
      return row.formattedSecondTime.toLowerCase();
    case "state":
      return row.statusLabel.toLowerCase();
    default:
      return "";
  }
}

export function getOrderRowFilterHaystack(
  row: OrderTableRow,
  column: string
): string {
  switch (column) {
    case "open":
      return formatDateTimeCompact(row.formattedTime).toLowerCase();
    case "symbol":
      return row.symbol.toLowerCase();
    case "ticket":
      return String(row.ticketNumber ?? row.ticket);
    case "type":
      return row.displayType.toLowerCase();
    case "lots":
      return String(row.lots);
    case "price":
      return row.price > 0 ? row.price.toFixed(row.digits) : "-";
    case "tp":
      return formatHistoryPrice(row.takeProfit, row.digits);
    case "sl":
      return formatHistoryPrice(row.stopLoss, row.digits);
    case "exec":
      return row.formattedSecondTime.toLowerCase();
    case "state":
      return row.statusLabel.toLowerCase();
    default:
      return "";
  }
}

export function getDealRowSortValue(
  row: DealTableRow,
  column: string
): string | number {
  switch (column) {
    case "time":
      return row.openTimeMs;
    case "symbol":
      return row.symbol.toLowerCase();
    case "deal":
      return row.ticketNumber;
    case "type":
      return row.displayType.toLowerCase();
    case "direction":
      return formatDirection(row.direction).toLowerCase();
    case "lots":
      return row.lots;
    case "price":
      return row.price;
    case "tp":
      return row.takeProfit;
    case "sl":
      return row.stopLoss;
    case "commission":
      return row.commission;
    case "pnl":
      return row.profit;
    case "change": {
      const change = dealPriceChangeParts(row);
      return change.numeric ?? -Infinity;
    }
    default:
      return "";
  }
}

export function getDealRowFilterHaystack(
  row: DealTableRow,
  column: string
): string {
  switch (column) {
    case "time":
      return formatDateTimeCompact(row.formattedTime).toLowerCase();
    case "symbol":
      return row.symbol.toLowerCase();
    case "deal":
      return String(row.ticketNumber);
    case "type":
      return row.displayType.toLowerCase();
    case "direction":
      return formatDirection(row.direction).toLowerCase();
    case "lots":
      return String(row.lots);
    case "price":
      return row.price.toFixed(row.digits);
    case "tp":
      return formatHistoryPrice(row.takeProfit, row.digits);
    case "sl":
      return formatHistoryPrice(row.stopLoss, row.digits);
    case "commission":
      return row.commission.toFixed(2);
    case "pnl":
      return formatPositionProfit(row.profit);
    case "change":
      return dealPriceChangeParts(row).display.toLowerCase();
    default:
      return "";
  }
}
